import _ from "lodash";
import React, { createContext } from "react";
import client from "lib/apolloClient/client";

import {
  addCollaboratorMutation,
  updateCollaboratorMutation,
  triggerCasExternalInviteEmailMutation,
  portCasExternalOldData,
} from "./Mutations";
import { getCasExternalInviteLinkQuery } from "./Queries";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import {
  getOrganizationStaffWithExternalUsersQuery,
  getProjectDetailsQuery,
  getProjectHeaderInfoQuery,
} from "Projects/modules/ProjectQueries";
import { getProjectHeaderInfoVariables } from "Projects/modules/Utils";
import {
  getExternalSupervisorInfoFromCache,
  writeExternalSupervisorInfoInCache,
} from "./GraphqlHelpers";
import {
  getProjectGroupExternalSupervisorsWithProjectsQuery,
  getProjectGroupSupervisorsWithTypeCountQuery,
} from "Projects/routes/Setup/routes/Invite/modules/InviteQueries";
import { CAS_EXTERNAL_COLLABORATOR_DESIGNATION } from "./constants/stringConstants";
import { CAS_EXTERNAL_USER_MODULE_NAME } from "Projects/Constants/stringConstants";
import { copyToClipboard } from "Utils/CopyToClipboard";
import { MentionOutlined } from "@toddle-edu/ds-icons";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import update from "immutability-helper";

export const createUser = params => {
  return async (dispatch, getState) => {
    const {
      userInfo,
      userType,
      projectGroupId,
      organizationId,
      extraParams: {
        type = "",
        getProjectGroupExternalSupervisorsWithProjectsVars = {},
      } = {},
    } = params;

    const academicYearIds = _.compact([getSelectedAcademicYearId(getState())]);

    const updatedGetProjectGroupExternalSupervisorsWithProjectsVars = update(
      getProjectGroupExternalSupervisorsWithProjectsVars,
      {
        filters: {
          $set: {
            ...getProjectGroupExternalSupervisorsWithProjectsVars?.filters,
            academicYearIds,
          },
        },
      }
    );

    const refetchQueries = [];
    switch (type) {
      case "invite_external_supervisor_screen_cas":
        {
          refetchQueries.push(
            {
              query: getProjectGroupExternalSupervisorsWithProjectsQuery,
              variables: updatedGetProjectGroupExternalSupervisorsWithProjectsVars,
            },
            {
              query: getProjectGroupSupervisorsWithTypeCountQuery,
              variables: {
                projectGroupId,
                supervisorFilters: {
                  supervisorType: "EXTERNAL",
                  approvalStatus: ["PENDING", "APPROVED"],
                  academicYearIds,
                },
              },
            }
          );
        }
        break;
      default: {
        refetchQueries.push({
          query: getOrganizationStaffWithExternalUsersQuery,
          variables: {
            organizationId,
            filters: {},
            externalSupervisorFilters: {
              moduleNames: [CAS_EXTERNAL_USER_MODULE_NAME],
            },
          },
        });
      }
    }

    try {
      const response = await client.mutate({
        mutation: addCollaboratorMutation,
        variables: {
          input: {
            email: userInfo.email,
            userType,
            firstName: userInfo.firstName,
            lastName: userInfo.lastName,
            middleName: userInfo.middleName,
            otherDetails: {
              organizationName: userInfo.organization,
              phoneNumber: userInfo.contact,
            },
            projectGroupDetails: {
              projectGroupId,
            },
            designation: _.isEqual(userType, "staff")
              ? CAS_EXTERNAL_COLLABORATOR_DESIGNATION
              : undefined,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries,
      });

      const result = _.get(response, "data.platform.addUserV2", {});
      const { isSuccess, warning, user } = result;

      const warningCode = _.get(warning, "code", "");

      return { isSuccess, warningCode, userDetails: user };
    } catch (e) {
      console.error(e);
    }
  };
};

export const updateUser = params => {
  return async () => {
    const { userInfo, shouldUpdateCache = true } = params;

    const cachedData = getExternalSupervisorInfoFromCache({ id: userInfo.id });

    const updatedData = {
      id: userInfo.id,
      email: userInfo.email,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      moduleNames: [CAS_EXTERNAL_USER_MODULE_NAME],
    };

    const updatedOtherDetails = {
      organizationName: userInfo.organization,
      phoneNumber: userInfo.contact,
    };

    try {
      const response = await client.mutate({
        mutation: updateCollaboratorMutation,
        variables: {
          input: { ...updatedData, otherDetails: updatedOtherDetails },
        },
      });

      const result = _.get(response, "data.platform.updateUserV2", {});
      const { isSuccess, warning } = result;

      const warningCode = _.get(warning, "code", "");
      if (shouldUpdateCache && isSuccess && cachedData) {
        writeExternalSupervisorInfoInCache({
          id: userInfo.id,
          data: {
            ...cachedData,
            ...updatedData,
            otherDetails: {
              ...(cachedData.otherDetails ? cachedData.otherDetails : {}),
              ...updatedOtherDetails,
            },
          },
        });
      }

      return { isSuccess, warningCode };
    } catch (e) {
      console.error(e);
      if (shouldUpdateCache)
        writeExternalSupervisorInfoInCache({
          id: userInfo.id,
          data: cachedData,
        });
    }
  };
};

export const triggerCasExternalInviteEmail = params => {
  return async dispatch => {
    const { projectGroupId, supervisorId, supervisorEmail } = params;

    try {
      await client.mutate({
        mutation: triggerCasExternalInviteEmailMutation,
        variables: {
          input: {
            projectGroupId,
            supervisorIds: [supervisorId],
          },
        },
      });

      dispatch(
        setToastMsg({
          type: "info",
          prefixIcon: <MentionOutlined />,
          msg: "toastMsgs:an_email_invite_has_been_sent_to_email",
          locale_params: [
            {
              key: "email",
              value: supervisorEmail,
              isPlainText: true,
            },
          ],
          timeOut: 5000,
        })
      );
    } catch (e) {
      console.error(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const copyCasExternalInviteLink = params => {
  return async dispatch => {
    const { supervisorId } = params;

    const copyLinkToClipboard = inviteLink => {
      copyToClipboard(inviteLink, () => {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:copied_capital",
            type: "success",
          })
        );
      });
    };

    try {
      const result = await client.query({
        query: getCasExternalInviteLinkQuery,
        variables: {
          id: supervisorId,
        },
        fetchPolicy: "network-only",
      });

      const inviteLink = _.get(
        result,
        "data.node.casExternalSupervisorInviteUrl",
        ""
      );
      if (inviteLink) {
        copyLinkToClipboard(inviteLink);
      }
    } catch (e) {
      console.warn(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const portCasExternalDataForProject = params => {
  return async (dispatch, getState) => {
    const {
      projectGroupId,
      supervisorId,
      supervisorToPort: emailToPort,
      projectId,
      projectGroupType,
      projectGroupCategoryType,
    } = params;
    const academicYearId = getSelectedAcademicYearId(getState());
    try {
      await client.mutate({
        mutation: portCasExternalOldData,
        variables: {
          input: {
            projectGroupId,
            supervisorId,
            emailToPort,
          },
        },
        refetchQueries: [
          {
            query: getProjectDetailsQuery,
            variables: {
              id: projectId,
              studentProjectPortfolioFilter: {
                projectGroupTypes: [projectGroupType],
              },
              academicYearId,
              completionStatusFilters: {
                academicYearIds: [academicYearId],
              },
            },
            context: {
              useHttpLink: true,
            },
          },
          {
            query: getProjectHeaderInfoQuery,
            variables: getProjectHeaderInfoVariables({
              projectId,
              projectGroupType,
              academicYearId,
              projectGroupCategoryType,
            }),
          },
        ],
        awaitRefetchQueries: true,
      });
    } catch (e) {
      console.error(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const CollaboratorV2Context = createContext();
